// How each kind of thing looks in 3D: the model from the library (packages/game/models, the Kenney kits) that
// draws it, and which of the model's materials take the thing's own colour (a sofa's fabric, a table's wood),
// the way Lagos Life tints the same kit. A thing whose kind has no model here keeps its block drawing. A place's
// prop can also name its own model in the content (the admin's choice wins over this default).

export type ModelStyle = {
  // the model, or several to pick from (the same thing in the same spot always gets the same one)
  model: string | string[];
  // materials painted in the thing's colour, and in a darker shade of it
  tint?: string[];
  shade?: string[];
  // a long thing (a counter) is a row of the model rather than one stretched copy
  row?: boolean;
};

const STYLES: Record<string, ModelStyle> = {
  bed: { model: 'furniture/bedDouble', tint: ['carpet'] },
  bed_single: { model: 'furniture/bedSingle', tint: ['carpet'] },
  wardrobe: { model: 'furniture/bookcaseClosedDoors', tint: ['wood'] },
  shower: { model: 'furniture/shower' },
  bath: { model: 'furniture/bathtub' },
  toilet: { model: 'furniture/toilet' },
  sink: { model: 'furniture/bathroomSink' },
  cooker: { model: 'furniture/kitchenStove' },
  cooker_range: { model: 'furniture/kitchenStoveElectric' },
  fridge: { model: 'furniture/kitchenFridgeLarge' },
  table: { model: 'furniture/table', tint: ['wood'] },
  chair: { model: 'furniture/chairCushion', tint: ['wood'] },
  desk: { model: 'furniture/desk', tint: ['wood'] },
  sofa: { model: 'furniture/loungeSofa', tint: ['carpet'] },
  armchair: { model: 'furniture/loungeChair', tint: ['carpet'] },
  shelf: { model: 'furniture/bookcaseOpen', tint: ['wood'] },
  tv: { model: 'furniture/televisionModern', tint: ['metalDark'] },
  rug: { model: 'furniture/rugRectangle', tint: ['carpet'], shade: ['carpetDarker'] },
  plant: { model: 'furniture/pottedPlant' },
  lamp: { model: 'furniture/lampRoundFloor' },
  counter: { model: 'furniture/kitchenBar', tint: ['wood'], row: true },
  stool: { model: 'furniture/stoolBar', tint: ['carpet'] },
  tree: { model: ['nature/tree_default', 'nature/tree_oak', 'nature/tree_detailed', 'nature/tree_fat'] },
};

// the style for a thing: by its own id first (a bath is a "shower" shape), then by its shape
export function modelStyle(shape: string, id?: string, model?: string): ModelStyle | undefined {
  if (model) return { model };
  return (id && STYLES[id]) || STYLES[shape];
}

// one model out of a style's choices, the same every time for the same spot
export function pickModel(style: ModelStyle, x = 0, z = 0) {
  if (typeof style.model === 'string') return style.model;
  const index = Math.abs(Math.round(x * 7.3 + z * 13.1)) % style.model.length;
  return style.model[index]!;
}
